import { Profile } from "@/types/profile";
import { Button } from "@/components/ui/button";
import { Facebook, Twitter, Instagram, Youtube, Linkedin, Github, Globe, Music, Apple, Link as LinkIcon } from "lucide-react";

interface SocialLinksProps {
  profile: Profile;
}

export const SocialLinks = ({ profile }: SocialLinksProps) => {
  const getSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'facebook': return Facebook;
      case 'twitter': return Twitter;
      case 'instagram': return Instagram;
      case 'youtube': return Youtube;
      case 'linkedin': return Linkedin;
      case 'github': return Github;
      case 'website': return Globe;
      case 'spotify': return Music;
      case 'apple': return Apple;
      case 'tiktok': return LinkIcon; // Using LinkIcon as fallback for TikTok since we don't have a specific icon
      default: return LinkIcon;
    }
  };

  const generateSocialLink = (platform: string, url: string) => {
    // If the URL already has http:// or https://, use it directly
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // Otherwise, generate a proper URL based on the platform
    switch(platform.toLowerCase()) {
      case 'website':
        return `https://${url}`;
      case 'twitter':
        return `https://twitter.com/${url}`;
      case 'facebook':
        return `https://facebook.com/${url}`;
      case 'instagram':
        return `https://instagram.com/${url}`;
      case 'linkedin':
        return url.includes('linkedin.com') ? `https://${url}` : `https://linkedin.com/in/${url}`;
      case 'youtube':
        return url.includes('youtube.com') ? `https://${url}` : `https://youtube.com/${url}`;
      case 'github':
        return `https://github.com/${url}`;
      case 'tiktok':
        return `https://tiktok.com/@${url}`;
      case 'spotify':
        return url.includes('spotify.com') ? `https://${url}` : `https://open.spotify.com/show/${url}`;
      case 'apple':
        return url.includes('apple.com') ? `https://${url}` : `https://podcasts.apple.com/podcast/${url}`;
      default:
        return `https://${platform}.com/${url}`;
    }
  };

  if (!profile.socials || Object.keys(profile.socials).length === 0) {
    return null;
  }

  return (
    <div className="flex-1 flex flex-col justify-center mt-6 md:mt-16">
      <h2 className="text-lg font-medium mb-3">Connect with {profile.full_name || profile.username}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {Object.entries(profile.socials).map(([platform, url], index) => {
          if (!platform || !url) return null;
          
          const Icon = getSocialIcon(platform);
          const platformUrl = generateSocialLink(platform, url);
          
          return (
            <Button 
              key={index} 
              variant="outline"
              size="sm"
              className="justify-start bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 shadow-sm"
              onClick={() => window.open(platformUrl, '_blank')}
            >
              <Icon className="h-4 w-4 mr-2 text-primary" strokeWidth={1.5} />
              <span className="text-xs font-medium truncate capitalize">{platform}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};
